import type { ReactElement } from 'react';

import { formatDateTime } from '@/lib/time';
import { CardFrame, QueryBody } from '@/components/ui/card-frame';
import { Pagination } from '@/components/ui/Pagination';
import { Select } from '@/components/ui/Select';

import { emptyText, kindLabel, STATUS_FILTERS, statusBadge } from '../presentation';
import type { DocumentRequest } from '../types';
import type { StatusFilter } from '../use-requests-url';
import { REQUEST_PAGE_SIZE, useRequestList } from '../use-document-requests';

// Bandeja de solicitudes de documento (G3-1, D11): Calidad ve todas las de la empresa
// activa y puede acotar a las suyas; el resto ve solo las propias. Los filtros viven en
// la URL (los maneja quien la monta). En móvil la tabla se lee como tarjetas.

interface Props {
  status: StatusFilter;
  mine: boolean;
  page: number;
  /** Puede ver las de toda la empresa (Calidad). */
  canSeeAll: boolean;
  onStatus: (s: StatusFilter) => void;
  onMine: (mine: boolean) => void;
  onPage: (page: number) => void;
  onOpen: (id: string) => void;
}

function what(r: DocumentRequest): string {
  return r.kind === 'modificacion' && r.target !== null
    ? `${kindLabel(r.kind)} · ${r.target.code}`
    : kindLabel(r.kind);
}

export function RequestsView(p: Props): ReactElement {
  const query = useRequestList({
    status: p.status === 'all' ? null : p.status,
    mine: p.mine,
    page: p.page,
  });

  return (
    <CardFrame title="Solicitudes de documentos" className="rise d1">
      <div className="cb filters">
        <label className="fld">
          <span className="fld-lab">Estado</span>
          <Select
            value={p.status}
            options={STATUS_FILTERS.map((f) => ({ value: f.value, label: f.label }))}
            onChange={(v) => p.onStatus(v as StatusFilter)}
          />
        </label>
        {p.canSeeAll && (
          <label className="chk">
            <input type="checkbox" checked={p.mine} onChange={(e) => p.onMine(e.target.checked)} />
            Solo las mías
          </label>
        )}
      </div>
      <QueryBody
        query={query}
        rows={4}
        // Vacía en la página 1 = no hay solicitudes; más allá (enlace viejo o tras cancelar
        // la última de la página) se ofrece volver a la primera (revisión adversarial B9).
        isEmpty={(d) => d.rows.length === 0 && p.page <= 1}
        emptyText={emptyText(p.status)}
      >
        {(d) =>
          d.rows.length === 0 ? (
            <div className="cb">
              <p className="fld-note">Esta página ya no tiene solicitudes.</p>
              <button type="button" className="btn mut sm" onClick={() => p.onPage(1)}>
                Ir a la primera página
              </button>
            </div>
          ) : (
            <>
              <div className="t-scroll">
                <table className="t t-cards">
                  <caption className="sr-only">Solicitudes de documentos</caption>
                  <thead>
                    <tr>
                      <th scope="col">Solicitud</th>
                      <th scope="col">Tipo</th>
                      <th scope="col">Proceso</th>
                      <th scope="col">Solicitante</th>
                      <th scope="col">Fecha</th>
                      <th scope="col">Estado</th>
                      <th scope="col" className="t-act">
                        Acciones
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {d.rows.map((r) => {
                      const badge = statusBadge(r.status);
                      return (
                        <tr key={r.id}>
                          <td data-label="Solicitud">
                            <strong>{r.proposedTitle}</strong>
                            <div className="soft">{what(r)}</div>
                          </td>
                          <td data-label="Tipo">{r.documentType.name}</td>
                          <td data-label="Proceso">{r.processArea.name}</td>
                          <td data-label="Solicitante">
                            {r.requester.displayName ?? 'Persona sin nombre'}
                          </td>
                          <td data-label="Fecha" className="dim-mono">
                            {formatDateTime(r.createdAt)}
                          </td>
                          <td data-label="Estado">
                            <span className={`badge ${badge.cls}`}>{badge.label}</span>
                          </td>
                          <td data-label="Acciones" className="t-act">
                            <button
                              type="button"
                              className="btn mut sm"
                              aria-label={`Ver solicitud ${r.proposedTitle}`}
                              onClick={() => p.onOpen(r.id)}
                            >
                              Ver
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              {d.total > REQUEST_PAGE_SIZE && (
                <Pagination
                  page={p.page}
                  pageSize={REQUEST_PAGE_SIZE}
                  total={d.total}
                  busy={query.isFetching}
                  onPage={p.onPage}
                />
              )}
            </>
          )
        }
      </QueryBody>
    </CardFrame>
  );
}
